import { useQuery } from "@tanstack/react-query";
import { AUDITED_READ_OPTIONS } from "@/shared/api/audited-read";
import { httpClient } from "@/shared/api/http-client";
import { keepRowsWithinScope } from "@/shared/api/scoped-placeholder";
import { buildOrganizationsQuery, normalizeSearchTerm } from "./build-organizations-query";
import { ORGANIZATIONS_QUERY_KEY } from "./constants";
import type { AdminOrganizationsList, OrganizationsListParams } from "./types";

function listKey(params: OrganizationsListParams) {
  return [
    ...ORGANIZATIONS_QUERY_KEY,
    "list",
    normalizeSearchTerm(params.search),
    params.kind ?? "all",
    params.page,
  ] as const;
}

/**
 * `GET /api/admin/organizations` — organizations of both ADR-0009 partitions,
 * newest first. The search matches the organization or owner display name
 * (never an email). Needs `org.members.read`; audited and throttled by the
 * API, so no retry and no refetch on focus. Rows stay while paging but never
 * stand in for the result of another search or kind.
 */
export function useOrganizationsList(params: OrganizationsListParams) {
  const queryKey = listKey(params);
  return useQuery({
    queryKey,
    queryFn: () =>
      httpClient<AdminOrganizationsList>(
        `/api/admin/organizations${buildOrganizationsQuery(params)}`,
      ),
    placeholderData: keepRowsWithinScope<AdminOrganizationsList>(queryKey),
    ...AUDITED_READ_OPTIONS,
  });
}
